import {
  Archive,
  ArrowLeft,
  ArrowSquareOut,
  ArrowUUpLeft,
  Calendar,
  CalendarBlank,
  ChatCircle,
  Check,
  Copy,
  Crosshair,
  Download,
  Funnel,
  IconProps,
  Info,
  Lifebuoy,
  MapTrifold,
  MinusCircle,
  PaperPlaneTilt,
  PencilSimple,
  PencilSimpleLine,
  PlusCircle,
  Prohibit,
  QrCode,
  SignOut,
  SlidersHorizontal,
  Trash,
  UserList,
  UserSwitch,
  X,
} from "@phosphor-icons/react";
import { ShoppingCart } from "lucide-react";
import { forwardRef, PropsWithChildren, ReactNode } from "react";
import { cn } from "../../utils";
import { Button, ButtonProps } from "./Button";

export interface IconButtonProps extends ButtonProps {
  className?: string;
  labelClassName?: string;
  svgProps?: IconProps;
  svgType: SvgType;
  label?: ReactNode;
}

export type SvgType =
  | "x"
  | "info"
  | "minusCircle"
  | "plusCircle"
  | "copy"
  | "trash"
  | "crosshair"
  | "arrowLeft"
  | "arrowUUpLeft"
  | "arrowSquareOut"
  | "funnel"
  | "check"
  | "chatCircle"
  | "paperPlaneTilt"
  | "export"
  | "download"
  | "calendar"
  | "calendarBlank"
  | "prohibit"
  | "forbidden"
  | "pencilSimple"
  | "pencilSimpleLine"
  | "slidersHorizontal"
  | "lifeBuoy"
  | "mapTrifold"
  | "userList"
  | "signOut"
  | "qrCode"
  | "userSwitch"
  | "cart"
  | "archive";

const svgIcons: Record<SvgType, React.ComponentType<IconProps>> = {
  x: X,
  info: Info,
  minusCircle: MinusCircle,
  plusCircle: PlusCircle,
  copy: Copy,
  trash: Trash,
  crosshair: Crosshair,
  arrowLeft: ArrowLeft,
  arrowUUpLeft: ArrowUUpLeft,
  arrowSquareOut: ArrowSquareOut,
  export: ArrowSquareOut,
  funnel: Funnel,
  check: Check,
  chatCircle: ChatCircle,
  paperPlaneTilt: PaperPlaneTilt,
  download: Download,
  calendar: Calendar,
  calendarBlank: CalendarBlank,
  prohibit: Prohibit,
  forbidden: Prohibit,
  pencilSimple: PencilSimple,
  pencilSimpleLine: PencilSimpleLine,
  slidersHorizontal: SlidersHorizontal,
  lifeBuoy: Lifebuoy,
  mapTrifold: MapTrifold,
  signOut: SignOut,
  userList: UserList,
  qrCode: QrCode,
  userSwitch: UserSwitch,
  cart: ShoppingCart,
  archive: Archive,
};

export const IconButton = forwardRef<HTMLButtonElement, PropsWithChildren<IconButtonProps>>(
  (
    {
      className,
      labelClassName,
      svgType,
      svgProps,
      label,
      variant = "transparent",
      children,
      ...props
    },
    ref,
  ) => {
    const SvgIcon = svgIcons[svgType];
    label = children || label;

    return (
      <Button
        ref={ref}
        size="icon"
        className={cn("flex w-fit items-center px-1", { "gap-1.5 px-2": label }, className)}
        variant={variant}
        {...props}
      >
        <div>
          <SvgIcon size={24} {...svgProps} />
        </div>
        {label && <div className={cn("text-base font-semibold", labelClassName)}>{label}</div>}
      </Button>
    );
  },
);
IconButton.displayName = "IconButton";

export default IconButton;
